import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FiCheck } from 'react-icons/fi';
import TzamunAiIcon from './TzamunAiIcon';

/**
 * Shared TzamunAI loading animation component.
 * Displays glowing aura, pulsing TzamunAI mark, shimmer progress bar,
 * ticking steps, and optional sweeping chips.
 *
 * @param {Object} props
 * @param {string[]} props.steps - Array of translated step strings (1-4).
 * @param {string[]} [props.chips] - Optional array of translated strings that sweep.
 * @param {string} [props.title] - Optional translated title line.
 * @param {number} [props.stepMs=900] - Interval in milliseconds between step advances.
 * @param {boolean} [props.compact=false] - Inline compact version for tight places.
 * @param {string} [props.className=''] - Additional CSS classes.
 * @param {{key: string, label: string}[]} [props.stages] - REAL stages, in order. With
 *   `reached` (from useAiProgress) a step ticks only when the server has marked it.
 * @param {string[]} [props.reached=[]] - Stage keys the server has reached so far.
 * @param {number} [props.expectedSec=15] - Typical wait; the bar eases toward 95% over it and never finishes.
 */
export default function AiThinking({
  steps = [],
  chips,
  title,
  stepMs = 900,
  compact = false,
  className = '',
  expectedSec = 15,
  stages,
  reached = [],
}) {
  // Real-stage mode: the current step is the last stage the server reported.
  const real = Array.isArray(stages) && stages.length > 0;
  if (real) {
    // eslint-disable-next-line no-param-reassign
    steps = stages.map((s) => s.label);
  }
  const realIndex = real
    ? Math.max(0, ...reached.map((k) => stages.findIndex((s) => s.key === k)))
    : 0;
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const shown = real ? realIndex : activeStep;
  const [elapsed, setElapsed] = useState(0);
  const [activeChip, setActiveChip] = useState(0);

  // Keyed on the COUNT, not the array: callers build `steps` inline, so the
  // array is new on every render and an array dependency would restart the
  // timer each time the parent re-renders, freezing the animation.
  const stepCount = steps ? steps.length : 0;
  const chipCount = chips ? chips.length : 0;

  useEffect(() => {
    if (real || stepCount <= 1) return;
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev < stepCount - 1 ? prev + 1 : prev));
    }, stepMs);
    return () => clearInterval(timer);
  }, [real, stepCount, stepMs]);

  useEffect(() => {
    if (chipCount <= 1) return;
    const timer = setInterval(() => {
      setActiveChip((prev) => (prev + 1) % chipCount);
    }, 280);
    return () => clearInterval(timer);
  }, [chipCount]);

  // Owner, 2026-10-03: a loader that stops moving reads as stuck. A seconds
  // counter ticks, the bar keeps easing toward 95% (never 100% before the
  // answer), and once the last step is reached a "still working" line shows.
  useEffect(() => {
    const timer = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);

  const progressPercent = Math.max(
    8,
    Math.round(95 * (1 - Math.exp(-elapsed / Math.max(1, expectedSec / 2))))
  );
  const onLastStep = stepCount <= 1 || shown === stepCount - 1;
  const stillWorking = onLastStep && elapsed >= 4;
  const dots = (
    <span aria-hidden="true" className="inline-flex gap-0.5 ms-1 align-middle">
      {[0, 150, 300].map((d) => (
        <span
          key={d}
          className="w-1 h-1 rounded-full bg-current motion-safe:animate-bounce"
          style={{ animationDelay: `${d}ms` }}
        />
      ))}
    </span>
  );
  const footer = (
    <p
      className="text-[11px] text-content-muted text-center tabular-nums"
      data-testid="ai-thinking-footer"
    >
      {stillWorking && <span className="me-2">{t('ai.thinking.stillWorking')}</span>}
      <span>{t('ai.thinking.elapsed', { seconds: elapsed })}</span>
    </p>
  );

  if (compact) {
    const currentStepText = steps[shown] || '';
    return (
      <div
        role="status"
        aria-live="polite"
        className={`relative overflow-hidden rounded-xl border border-accent/30 bg-surface p-3 sm:p-4 shadow-sm ${className}`}
      >
        <div className="absolute -top-6 -start-6 w-16 h-16 rounded-full bg-accent/15 blur-xl pointer-events-none motion-safe:animate-pulse" />
        <div className="flex items-center gap-3">
          <div className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-surface-2 border border-accent/30 shadow-sm shrink-0">
            <div className="absolute inset-0 rounded-xl bg-accent/20 blur-sm motion-safe:animate-pulse" />
            <TzamunAiIcon size={18} className="relative z-10 motion-safe:animate-pulse" />
          </div>
          <div className="flex-1 min-w-0">
            {title && (
              <p className="text-[11px] font-medium text-content-muted truncate mb-0.5">{title}</p>
            )}
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold text-accent truncate">
                {currentStepText}
                {dots}
              </span>
              <span className="text-[10px] text-content-muted tabular-nums shrink-0">
                {t('ai.thinking.elapsed', { seconds: elapsed })}
              </span>
            </div>
            <div className="w-full bg-surface-3 h-1.5 rounded-full overflow-hidden relative mt-1.5">
              <div
                className="h-full rounded-full bg-gradient-to-r from-accent/50 via-accent to-accent-alt relative overflow-hidden transition-all duration-300"
                data-testid="ai-thinking-bar"
                style={{ width: `${progressPercent}%` }}
              >
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent motion-safe:animate-shimmer-slide" />
              </div>
            </div>
          </div>
        </div>
        {/* Render all step texts for screen readers in compact mode */}
        <div className="sr-only">
          {steps.map((s, idx) => (
            <span key={idx}>{s}</span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative overflow-hidden rounded-xl border border-accent/30 bg-surface p-5 shadow-sm ${className}`}
    >
      <div className="absolute -top-10 -start-10 w-28 h-28 rounded-full bg-accent/15 blur-2xl pointer-events-none motion-safe:animate-pulse" />
      <div className="absolute -bottom-10 -end-10 w-28 h-28 rounded-full bg-accent-alt/15 blur-2xl pointer-events-none motion-safe:animate-pulse" />

      <div className="relative flex items-center justify-center w-12 h-12 rounded-2xl bg-surface-2 border border-accent/30 shadow-sm mx-auto mb-3">
        <div className="absolute inset-0 rounded-2xl bg-accent/20 blur-md motion-safe:animate-pulse" />
        <TzamunAiIcon size={24} className="relative z-10 motion-safe:animate-pulse" />
      </div>

      {title && (!chips || chips.length === 0) && (
        <p className="text-xs font-semibold text-content text-center mb-2">{title}</p>
      )}

      <div className="w-full bg-surface-3 h-2 rounded-full overflow-hidden relative mb-4">
        <div
          className="h-full rounded-full bg-gradient-to-r from-accent/50 via-accent to-accent-alt relative overflow-hidden transition-all duration-300"
          data-testid="ai-thinking-bar"
          style={{ width: `${progressPercent}%` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent motion-safe:animate-shimmer-slide" />
        </div>
      </div>

      <div className="space-y-1.5 max-w-sm mx-auto mb-3">
        {steps.map((stepText, idx) => {
          const isPast = idx < shown;
          const isCurrent = idx === shown;
          return (
            <div
              key={idx}
              className={`flex items-center gap-2 text-xs transition-colors duration-300 ${
                isCurrent
                  ? 'text-accent font-semibold'
                  : isPast
                    ? 'text-content-secondary'
                    : 'text-content-muted'
              }`}
            >
              <span
                className={`flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold shrink-0 ${
                  isPast
                    ? 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400'
                    : isCurrent
                      ? 'bg-accent text-white dark:text-slate-900 motion-safe:animate-pulse'
                      : 'bg-surface-3 text-content-muted'
                }`}
              >
                {isPast ? <FiCheck size={10} /> : idx + 1}
              </span>
              <span>
                {stepText}
                {isCurrent && dots}
              </span>
            </div>
          );
        })}
      </div>

      {footer}

      {chips && chips.length > 0 && (
        <div className="mt-3 flex flex-col items-center gap-1.5 pt-2 border-t border-edge/60">
          {title && <span className="text-[11px] text-content-muted shrink-0">{title}</span>}
          <div className="flex flex-wrap justify-center gap-1 py-0.5">
            {chips.map((chip, idx) => (
              <span
                key={typeof chip === 'string' ? chip : idx}
                className={`px-2 py-0.5 rounded-full text-[11px] whitespace-nowrap transition-all duration-300 ${
                  idx === activeChip
                    ? 'bg-accent text-white dark:text-slate-900 font-semibold shadow-sm scale-105'
                    : 'bg-surface-2 text-content-muted border border-edge opacity-60'
                }`}
              >
                {chip}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
